/**
 * LST — Leader Standard Work (Trabalho Padronizado dos Líderes)
 * Contratos tipados compartilhados pelo módulo "Rotinas de Liderança".
 */

import type { OrgMember } from '../../hooks/useOrgMembers';

// ─── Domínio ────────────────────────────────────────────────

/** Camada de liderança alvo de um padrão de rotina. */
export type LstCargo =
  | 'lider'
  | 'supervisor'
  | 'coordenador'
  | 'gerente'
  | 'diretor'
  | 'apoio';

/** Ordem de exibição dos cargos LST (Configuração / hierarquia). */
export const LST_CARGO_ORDER: LstCargo[] = [
  'lider',
  'supervisor',
  'coordenador',
  'gerente',
  'diretor',
  'apoio',
];

/** Gatilho de frequência da rotina. */
export type LstFrequency = 'horaria' | 'diaria' | 'semanal' | 'mensal';

/** Tipo de atividade na rotina (execução diferenciada no Gemba). */
export type LstActivityKind = 'standard' | 'reuniao_camada';

/** Bloco do turno (linha do tempo Início → Meio → Fim). */
export type LstPeriod = 'inicio' | 'meio' | 'fim';

/** Status da execução diária de uma rotina. */
export type LstStatus =
  | 'realizado'
  | 'realizado_com_desvio'
  | 'nao_realizado'
  | 'processo_parado';

/** Motivo da exceção "Processo Parado" (linha/área indisponível para checagem). */
export type LstMotivoParada = 'falta_op' | 'setup' | 'quebra' | 'falta_material';

/** Categoria/cor da rotina (eixo de gestão visual). */
export type LstCategory = 'seguranca' | 'qualidade' | 'producao' | 'apoio';

/** Modo principal da Instrução de Trabalho Padrão (SWI). */
export type LstSwiMode = 'text' | 'document' | 'checklist';

/** Sub-passo de verificação interna da SWI. */
export interface LstSwiChecklistItem {
  id: string;
  text: string;
}

/** Escopo de aplicação da rotina padrão. */
export type LstTemplateScope = 'cargo' | 'user';

export interface LstTemplate {
  id: string;
  organization_id: string;
  target_role: LstCargo;
  /** cargo = padrão macro do cargo; user = exceção individual. */
  scope: LstTemplateScope;
  /** Preenchido quando scope = user. */
  assigned_user_id: string | null;
  title: string;
  description: string | null;
  frequency: LstFrequency;
  period: LstPeriod;
  scheduled_time: string | null;
  category: LstCategory | null;
  is_critical: boolean;
  sort_order: number;
  active: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  /** Orientações passo a passo (rich text / HTML). */
  swi_text: string | null;
  /** URL do POP visual (imagem/PDF). */
  swi_doc_url: string | null;
  /** Sub-passos de verificação interna. */
  swi_checklist: LstSwiChecklistItem[];
  /** Modo principal de exibição da SWI. */
  swi_mode: LstSwiMode;
  /** Duração prevista em minutos (Yamazumi). */
  duration_minutes: number;
  /** Antecedência do alerta (5, 10 ou 15 min). */
  alert_lead_minutes: 5 | 10 | 15;
  /** Canal de aviso: push | andon | both. */
  alert_channel: LstAlertChannel;
  /** Indicador global vinculado (tabela indicators). */
  linked_indicator_id: string | null;
  /** Nome livre quando não há KPI global (combobox híbrido). */
  linked_indicator_name: string | null;
  /** Meta local do indicador (override ou indicador custom). */
  linked_indicator_target: number | null;
  /** Direção da meta para indicadores custom. */
  linked_indicator_direction: 'higher_is_better' | 'lower_is_better';
  /** Reunião de Camada usa drawer de ata em vez do raio-X padrão. */
  activity_kind: LstActivityKind;
}

export const LST_ACTIVITY_KIND_LABELS: Record<LstActivityKind, string> = {
  standard: 'Rotina padrão',
  reuniao_camada: 'Reunião de Camada',
};

export type LstAlertChannel = 'push' | 'andon' | 'both';

export const LST_ALERT_CHANNEL_LABELS: Record<LstAlertChannel, string> = {
  push: 'Push (notificação)',
  andon: 'Andon visual na tela',
  both: 'Push + Andon',
};

export const LST_ALERT_LEAD_OPTIONS: Array<5 | 10 | 15> = [5, 10, 15];

/** Ata de revisão do padrão entre líder e liderado. */
export interface LstRevisaoAta {
  id: string;
  organization_id: string;
  target_role: LstCargo;
  liderado_user_id: string | null;
  reviewer_user_id: string;
  notes: string;
  created_at: string;
}

/** Marco registrado após revisão — linha vertical nos gráficos de tendência. */
export interface LstMilestone {
  id: string;
  organization_id: string;
  revisao_id: string | null;
  target_role: LstCargo;
  milestone_date: string;
  label: string;
  notes: string | null;
  created_by: string | null;
  created_at: string;
}

export const LST_SWI_MODE_LABELS: Record<LstSwiMode, string> = {
  text: 'Texto / Passo a passo',
  document: 'Documento / POP visual',
  checklist: 'Questionário interno',
};

export interface LstExecucao {
  id: string;
  organization_id: string;
  template_id: string;
  user_id: string;
  data: string; // YYYY-MM-DD
  status: LstStatus;
  justificativa_desvio: string | null;
  action_id: string | null;
  /** Observação/nota de campo (memória de campo / Kaizen). */
  observacao: string | null;
  /** URL pública de foto anexada (auditoria visual 5S/Segurança). */
  anexo_url: string | null;
  /** Valor realizado do indicador vinculado, informado no gemba. */
  indicator_realized_value: number | null;
  created_at: string;
  updated_at: string;
}

/** Rotina do dia já mesclada com seu status de execução (view do "Meu Padrão"). */
export interface LstRotinaDoDia {
  template: LstTemplate;
  execucao: LstExecucao | null;
  status: LstStatus;
}

/** Aderência de um liderado em um dia específico (célula da matriz de calor). */
export type LstAderenciaNivel = 'verde' | 'laranja' | 'vermelho' | 'neutro';

export interface LstDiaAderencia {
  date: string; // YYYY-MM-DD
  nivel: LstAderenciaNivel;
  criticasTotal: number;
  criticasRealizadas: number;
  desvios: number;
  perdidas: number;
}

export interface LstLideradoAderencia {
  member: OrgMember;
  cargo: LstCargo;
  dias: LstDiaAderencia[];
  /** Desvios e perdas da semana — pauta da reunião de eficácia. */
  ocorrencias: LstOcorrencia[];
}

export interface LstOcorrencia {
  date: string;
  templateTitle: string;
  status: Extract<LstStatus, 'realizado_com_desvio' | 'nao_realizado'>;
  justificativa: string | null;
  isCritical: boolean;
}

// ─── Labels / mapeamentos ───────────────────────────────────

export const LST_CARGO_LABELS: Record<LstCargo, string> = {
  lider: 'Líder',
  supervisor: 'Supervisor',
  coordenador: 'Coordenador',
  gerente: 'Gerente',
  diretor: 'Diretor',
  apoio: 'Apoio',
};

/** Status de ação em reunião de camada. */
export type LstLayerActionStatus = 'aberta' | 'concluida';

/** Ação registrada em reunião de camada. */
export interface LstLayerAction {
  id: string;
  description: string;
  responsible: string;
  dueDate: string;
  status: LstLayerActionStatus;
}

/** Chave do par hierárquico em reuniões de camada. */
export type LstLayerMeetingKey =
  | 'lider-supervisor'
  | 'supervisor-coordenador'
  | 'coordenador-gerente'
  | 'gerente-diretor';

/** Ata estruturada de reunião de camada (persistida em lst_layer_meetings). */
export interface LstLayerMeeting {
  id: string;
  organization_id: string;
  meeting_date: string;
  layer_key: LstLayerMeetingKey;
  leader_user_id: string;
  subordinate_user_id: string;
  leader_cargo: LstCargo;
  subordinate_cargo: LstCargo;
  adherence_pct: number | null;
  duration_minutes: number | null;
  sections: Record<string, string>;
  actions: LstLayerAction[];
  evidence_urls: string[];
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export const LST_FREQUENCY_LABELS: Record<LstFrequency, string> = {
  horaria: 'Horária',
  diaria: 'Diária',
  semanal: 'Semanal',
  mensal: 'Mensal',
};

export const LST_PERIOD_LABELS: Record<LstPeriod, string> = {
  inicio: 'Início do Turno',
  meio: 'Meio do Turno',
  fim: 'Fim do Turno',
};

export const LST_PERIOD_ORDER: LstPeriod[] = ['inicio', 'meio', 'fim'];

export const LST_STATUS_LABELS: Record<LstStatus, string> = {
  realizado: 'Conforme (O)',
  realizado_com_desvio: 'Desvio (Δ)',
  nao_realizado: 'Não realizado (X)',
  processo_parado: 'Processo parado',
};

export const LST_CATEGORY_LABELS: Record<LstCategory, string> = {
  seguranca: 'Segurança',
  qualidade: 'Qualidade',
  producao: 'Produção',
  apoio: 'Apoio',
};

/**
 * Paleta por categoria alinhada ao quadro físico de gestão à vista:
 *   Segurança = Azul · Produção = Verde · Qualidade = Amarelo · Apoio = Cinza.
 * `border` é a faixa lateral de 4px (escopo); `dot`/`chip` usados em legendas.
 */
export const LST_CATEGORY_STYLES: Record<LstCategory, { dot: string; chip: string; border: string }> = {
  seguranca: {
    dot: 'bg-blue-500',
    chip: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    border: 'border-l-blue-500',
  },
  producao: {
    dot: 'bg-emerald-500',
    chip: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
    border: 'border-l-emerald-500',
  },
  qualidade: {
    dot: 'bg-amber-400',
    chip: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
    border: 'border-l-amber-400',
  },
  apoio: {
    dot: 'bg-slate-400',
    chip: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400',
    border: 'border-l-slate-300 dark:border-l-slate-600',
  },
};

export const LST_CATEGORY_ORDER: LstCategory[] = ['seguranca', 'producao', 'qualidade', 'apoio'];

/**
 * Mapeia o role_type da organização (operational/lead/manager/director)
 * para a camada de liderança do LST. Mapeamento ordinal por nível.
 */
export function mapRoleTypeToCargo(roleType: OrgMember['roleType']): LstCargo {
  switch (roleType) {
    case 'director':
      return 'diretor';
    case 'manager':
      return 'gerente';
    case 'lead':
      return 'lider';
    case 'operational':
      return 'apoio';
    default:
      return 'apoio';
  }
}

/** Refina cargo LST usando nome do papel na organização (workflow_roles_v9). */
export function resolveLstCargoFromContext(
  lstCargo: LstCargo,
  roleName?: string | null,
): LstCargo {
  const name = (roleName ?? '').toLowerCase();
  if (name.includes('diretor') || name.includes('president')) return 'diretor';
  if (name.includes('coordenador')) return 'coordenador';
  if (name.includes('gerente')) return 'gerente';
  if (name.includes('supervisor')) return 'supervisor';
  if (name.includes('líder') || name.includes('lider')) return 'lider';
  return lstCargo;
}
